import { apiClient } from './api';

export interface ConsultationResponse {
  id: string;
  template_id: string | null;
  patient_name: string;
  patient_species: string;
  patient_breed?: string;
  patient_age?: string;
  patient_gender?: string;
  sterilization_status?: string;
  patient_birth_date?: string;
  patient_weight?: number | null;
  client_name?: string;
  client_email?: string;
  vet_notes?: string;
  transcription: string | null;
  corrected_transcription: string | null;
  soap_report: SOAPReport | null;
  ai_suggestions: AISuggestions | null;
  chat_messages?: Array<{ role: 'user' | 'assistant'; content: string }> | null;
  diagnosis?: string | null;
  client_report?: string | null;
  client_report_language?: string | null;
  prescription?: string | null;
  extracted_drugs?: any[];
  status: string;
  created_at: string;
  updated_at: string;
}

export interface SOAPReport {
  subjectif: string;
  objectif: string;
  analyse: string;
  plan: string;
}

export interface AISuggestions {
  differential_diagnoses?: Array<{
    name: string;
    details: string[];
  }>;
  recommended_tests_priority1?: string[];
  recommended_tests_priority2?: string[];
  warnings?: string[];
  treatment_suggestions?: string[];
  rag_citations?: any[];
}

export interface StartConsultationRequest {
  patient_name?: string;
  patient_species?: string;
  patient_breed?: string;
  patient_age?: string;
  patient_gender?: string;
  sterilization_status?: string;
  patient_birth_date?: string;
  patient_weight?: number;
  client_name?: string;
  client_email?: string;
  vet_notes?: string;
  template_id?: string;
  prefill_with_template?: boolean;
}

export interface UploadAudioRequest {
  consultation_id: string;
  audio_file: Blob;
}

export interface TranscribeRequest {
  consultation_id: string;
  existing_report?: string;
}

export interface GenerateReportRequest {
  consultation_id: string;
}

export const consultationService = {
  /**
   * Start a new consultation
   */
  /**
   * Start a new consultation
   */
  startConsultation: async (data?: StartConsultationRequest) => {
    const response = await apiClient.post<any>(
      `/consultations/start`,
      data || {}
    );
    return {
      id: response.data.id,
      ...response.data,
    };
  },

  /**
   * Upload audio file for a consultation
   */
  uploadAudio: async ({ consultation_id, audio_file }: UploadAudioRequest) => {
    const formData = new FormData();
    formData.append('file', audio_file, 'consultation_audio.webm');

    const response = await apiClient.post<ConsultationResponse>(
      `/consultations/${consultation_id}/upload-audio`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Trigger transcription for uploaded audio and correct with Claude
   * The backend /transcribe endpoint handles both WhisperX transcription and Claude correction
   */
  transcribe: async ({ consultation_id, existing_report }: TranscribeRequest) => {
    // Send existing_report as FormData to match backend expectation
    const formData = new FormData();
    if (existing_report && existing_report.trim()) {
      formData.append('existing_report', existing_report);
    }

    // The backend /transcribe endpoint does both transcription AND correction in one call

    const response = await apiClient.post<any>(
      `/consultations/${consultation_id}/transcribe`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 600000, // 10 minutes to allow for initial model loading
      }
    );

    return {
      id: consultation_id,
      transcription: response.data.transcription,
      corrected_transcription: response.data.corrected_transcription,
      ...response.data,
    };
  },

  /**
   * Upload and transcribe a single ambient recording chunk, updating the report incrementally.
   */
  transcribeAmbientChunk: async (
    consultationId: string,
    audioChunk: Blob,
    existingReport: string,
    language: string = 'fr',
    previousTail: string = '',
  ): Promise<{
    updated_report: string;
    patient_name?: string;
    patient_species?: string;
    patient_breed?: string;
    patient_age?: string;
    patient_gender?: string;
    sterilization_status?: string;
    patient_weight?: string;
    extracted_drugs?: any[];
    chunk_transcription?: string;
  }> => {
    const formData = new FormData();
    formData.append('audio_file', audioChunk, 'chunk.webm');
    formData.append('existing_report', existingReport);
    formData.append('language', language);
    if (previousTail) formData.append('previous_tail', previousTail);

    const response = await apiClient.post(
      `/consultations/${consultationId}/transcribe-ambient-chunk`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000, // 2 minutes per chunk
      }
    );
    return response.data;
  },

  /**
   * Final consolidation pass — given the full accumulated transcript and current report,
   * returns a cleaned, contradiction-free, maximally-consolidated final report.
   */
  finalizeAmbientSession: async (
    consultationId: string,
    fullTranscription: string,
    existingReport: string,
    language: string = 'fr',
  ): Promise<{ final_report: string }> => {
    const formData = new FormData();
    formData.append('full_transcription', fullTranscription);
    formData.append('existing_report', existingReport);
    formData.append('language', language);

    const response = await apiClient.post(
      `/consultations/${consultationId}/finalize-ambient`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      }
    );
    return response.data;
  },

  /**
   * Generate SOAP report and AI suggestions
   */
  generateReport: async ({ consultation_id }: GenerateReportRequest) => {
    const response = await apiClient.post<ConsultationResponse>(
      `/consultations/${consultation_id}/generate-report`
    );
    return response.data;
  },

  /**
   * Get consultation by ID
   */
  getById: async (id: string) => {
    const response = await apiClient.get<ConsultationResponse>(
      `/consultations/${id}`
    );
    return response.data;
  },

  /**
   * Generate AI diagnostic suggestions
   */
  generateSuggestions: async ({
    consultation_id,
    custom_text
  }: {
    consultation_id: string;
    custom_text?: string;
  }) => {
    // Send as FormData to match backend expectation
    const formData = new FormData();
    formData.append('custom_text', custom_text || '');

    const response = await apiClient.post<any>(
      `/consultations/${consultation_id}/generate-suggestions`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },


  /**
   * List all consultations
   */
  getAllConsultations: async () => {
    const response = await apiClient.get<ConsultationResponse[]>(
      '/consultations/'
    );
    return response.data;
  },


  /**
   * Finish and save consultation
   */
  finishConsultation: async ({
    consultation_id,
    report_text,
    diagnosis,
    client_report,
    client_report_language,
    prescription,
  }: {
    consultation_id: string;
    report_text: string;
    diagnosis?: string;
    client_report?: string;
    client_report_language?: string;
    prescription?: string;
  }) => {
    const formData = new FormData();
    formData.append('report_text', report_text);
    if (diagnosis) {
      formData.append('diagnosis', diagnosis);
    }
    if (client_report) {
      formData.append('client_report', client_report);
    }
    if (client_report_language) {
      formData.append('client_report_language', client_report_language);
    }
    if (prescription) {
      formData.append('prescription', prescription);
    }

    const response = await apiClient.post<any>(
      `/consultations/${consultation_id}/finish`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Get consultation history for a patient
   */
  getPatientHistory: async (patientName: string) => {
    const response = await apiClient.get<ConsultationResponse[]>(
      `/consultations/history/patient`,
      {
        params: { name: patientName }
      }
    );
    return response.data;
  },

  /**
   * Ask a follow-up question to Claude AI
   */
  askFollowupQuestion: async ({
    consultation_id,
    question,
    conversation_history
  }: {
    consultation_id: string;
    question: string;
    conversation_history?: string;
  }) => {
    const formData = new FormData();
    formData.append('question', question);
    if (conversation_history) {
      formData.append('conversation_history', conversation_history);
    }

    const response = await apiClient.post<{
      question: string;
      answer: string;
      citations?: any[];
      usage: {
        input_tokens: number;
        output_tokens: number;
      };
    }>(
      `/consultations/${consultation_id}/ask-followup`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Ask a follow-up question with streaming response
   */
  askFollowupQuestionStream: async ({
    consultation_id,
    question,
    onToken,
    onCitations,
    onStatus,
    onDone,
    onError
  }: {
    consultation_id: string;
    question: string;
    onToken: (token: string) => void;
    onCitations: (citations: any[]) => void;
    onStatus: (status: string) => void;
    onDone: () => void;
    onError: (error: string) => void;
  }) => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || '';
      const baseURL = API_URL ? `${API_URL}/api/v1` : '/api/v1';
      const token = localStorage.getItem('access_token');

      const formData = new FormData();
      formData.append('question', question);

      const response = await fetch(`${baseURL}/consultations/${consultation_id}/ask-followup-stream`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      if (!reader) throw new Error("No reader available");

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          // Process any remaining data in buffer
          if (buffer.trim()) {
            const events = buffer.split('\n\n');
            for (const event of events) {
              processEvent(event);
            }
          }
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // Process complete events
        const events = buffer.split('\n\n');
        // Keep the last part which might be incomplete
        buffer = events.pop() || '';

        for (const event of events) {
          processEvent(event);
        }
      }

      function processEvent(event: string) {
        if (!event.trim()) return;

        const lines = event.split('\n');
        for (const line of lines) {
          if (line.startsWith('data:')) {
            try {
              const jsonStr = line.startsWith('data: ') ? line.slice(6) : line.slice(5);
              if (!jsonStr.trim()) return;

              const data = JSON.parse(jsonStr);

              switch (data.type) {
                case 'token':
                  onToken(data.content);
                  break;
                case 'citations':
                  onCitations(data.content);
                  break;
                case 'status':
                  onStatus(data.content);
                  break;
                case 'done':
                  onDone();
                  return;
                case 'error':
                  onError(data.content);
                  return;
              }
            } catch (e) {
              console.error("Error parsing SSE data:", e, "line:", line);
            }
          }
        }
      }

      onDone();
    } catch (err: any) {
      console.error("Stream error:", err);
      onError(err.message || 'Stream connection failed');
    }
  },

  /**
   * Generate client-friendly report from compte-rendu
   */
  generateClientReport: async ({
    consultation_id,
    compte_rendu,
    language,
  }: {
    consultation_id: string;
    compte_rendu: string;
    language: 'fr' | 'en';
  }) => {
    const formData = new FormData();
    formData.append('compte_rendu', compte_rendu);
    formData.append('language', language);

    const response = await apiClient.post<{
      client_report: string;
      usage: {
        input_tokens: number;
        output_tokens: number;
      };
    }>(
      `/consultations/${consultation_id}/generate-client-report`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Generate prescription with posology from compte-rendu
   */
  generatePrescription: async ({
    consultation_id,
    compte_rendu,
  }: {
    consultation_id: string;
    compte_rendu: string;
  }) => {
    const formData = new FormData();
    formData.append('compte_rendu', compte_rendu);

    const response = await apiClient.post<{
      prescription: string;
      usage: {
        input_tokens: number;
        output_tokens: number;
      };
    }>(
      `/consultations/${consultation_id}/generate-prescription`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Send client report via email
   */
  sendClientReportEmail: async (consultation_id: string, email?: string) => {
    const formData = new FormData();
    if (email) {
      formData.append('recipient_email', email);
    }

    const response = await apiClient.post<{
      success: boolean;
      message: string;
      task_id: string;
      recipient: string;
    }>(
      `/consultations/${consultation_id}/send-client-report-email`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Send alimentation/nutrition report via email (backend SMTP)
   */
  sendAlimentationReportEmail: async (consultation_id: string, recipient_email: string, report_content: string) => {
    const formData = new FormData();
    formData.append('recipient_email', recipient_email);
    formData.append('report_content', report_content);

    const response = await apiClient.post<{
      success: boolean;
      message: string;
      recipient: string;
    }>(
      `/consultations/${consultation_id}/send-alimentation-report-email`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  /**
   * Update consultation information (animal info, transcription, client report, prescription, etc.)
   */
  updateConsultation: async ({
    consultation_id,
    patient_name,
    patient_species,
    patient_breed,
    patient_age,
    patient_gender,
    sterilization_status,
    patient_weight,
    corrected_transcription,
    client_report,
    prescription,
    template_id,
  }: {
    consultation_id: string;
    patient_name?: string;
    patient_species?: string;
    patient_breed?: string;
    patient_age?: string;
    patient_gender?: string;
    sterilization_status?: string;
    patient_weight?: number | null;
    corrected_transcription?: string;
    client_report?: string;
    prescription?: string;
    template_id?: string | null;
  }) => {
    const formData = new FormData();
    if (patient_name !== undefined) formData.append('patient_name', patient_name);
    if (patient_species !== undefined) formData.append('patient_species', patient_species);
    if (patient_breed !== undefined) formData.append('patient_breed', patient_breed);
    if (patient_age !== undefined) formData.append('patient_age', patient_age);
    if (patient_gender !== undefined) formData.append('patient_gender', patient_gender);
    if (sterilization_status !== undefined) formData.append('sterilization_status', sterilization_status);
    if (patient_weight !== undefined) {
      formData.append('patient_weight', patient_weight === null ? '' : patient_weight.toString());
    }
    if (corrected_transcription !== undefined) {
      formData.append('corrected_transcription', corrected_transcription);
    }
    if (client_report !== undefined) {
      formData.append('client_report', client_report);
    }
    if (prescription !== undefined) {
      formData.append('prescription', prescription);
    }
    if (template_id !== undefined) {
      formData.append('template_id', template_id ?? '');
    }

    const response = await apiClient.patch<ConsultationResponse>(
      `/consultations/${consultation_id}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Send prescription via email
   */
  sendPrescriptionEmail: async (consultation_id: string, email?: string) => {
    const formData = new FormData();
    if (email) {
      formData.append('recipient_email', email);
    }

    const response = await apiClient.post<{
      success: boolean;
      message: string;
      task_id: string;
      recipient: string;
    }>(
      `/consultations/${consultation_id}/send-prescription-email`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Generate prescription item for a specific drug
   */
  generatePrescriptionItem: async (data: {
    drug_id: string;
    scenario_label?: string;
    patient_weight?: number | null;
    patient_species?: string;
    clinical_report?: string;
    current_prescription?: string;
  }) => {
    const response = await apiClient.post<{
      prescription_text: string;
    }>(
      `/drugs/generate-prescription`,
      data
    );
    return response.data;
  },

  /**
   * Delete a consultation
   */
  deleteConsultation: async (consultation_id: string) => {
    const response = await apiClient.delete(
      `/consultations/${consultation_id}`
    );
    return response.data;
  },
};
